/*
* Indicator component specific for Adalia M&E.
* 
* Project can be passed as a prop, but if not, this component expects a project-id context. Rendering without causes an error.
* 
*/
import React, { useEffect, useContext, useState } from 'react';

import { FaIcon } from '../../common/Icons.jsx';

import {FormContext} from '../form_context.jsx';
import { resolveComponentProps } from '../form_viewer.jsx';
import { cachedFetchJson } from '../../../helpers/fetch.js';
import { INDICATOR_TYPE } from '../../result_framework/indicator/IndicatorForm.jsx';

import {
  Mode,
  DataType,
  castDataType
} from '../common.jsx';
import Helpers from './helpers.jsx';
import Setting from './setting.jsx';

import GridLayout from './grid_layout.jsx'
import TextField from './text_field.jsx'
import DropdownSelection from './dropdown_selection.jsx'
import DropdownSelectionWithAsyncOptions from './dropdown_selection_with_async_options.jsx';
import Label from './label.jsx'
import RepeatableRegion from './repeatable_region.jsx';
import Resolver from '../component_resolver.jsx';

import ExternalEntityInput from '../../indicator_record/ExternalEntityInput.jsx';

import {
  result_framework_get_path,
  result_framework_list_common_indicators_for_programme_path,
  indicator_value_get_entity_columns_for_record_path,
  indicator_value_show_path
} from '~/routes';

const groupBy = (items, key) => items.reduce(
  (result, item) => ({
    ...result,
    [item[key]]: [
      ...(result[item[key]] || []),
      item,
    ],
  }), 
  {},
);
  

const SETTINGS_DUMMY_DATA = [
  {value: 0, label: '1.1 Example indicator'},
  {value: 1, label: '1.2 Example indicator'},
  {value: 2, label: '1.3 Example indicator'}
]

const INDICATOR_LEVEL_OPTIONS = [
  'project',
  'fund',
]

window.rfCache = {};

const getIndicators = (projectId, defaultOpt) => {        
  return new Promise((resolve, reject) => { 
    cachedFetchJson(result_framework_get_path({project_id: projectId})).then((res) => {
      if (res){
        const arr = Object.values(res.indicator)
          .filter((i) => i.id && i.subtype != INDICATOR_TYPE.aggregate)
          .map((i) => ({
            value: i.id,
            common_indicator_id: i.common_indicator_id,
            label: i.number + " " + i.title,
            unit_name: i.unit_name,
            disaggregation_type: i.disaggregation_type,
            disaggregations: i.disaggregations,
            additional_fields: i.additional_fields
          })); 
        resolve({
          options: [defaultOpt, ...arr],
          fetchingOptions: false
        })
      } else {
        reject("Invalid response")
      }
    })
  })
}

const getCommonIndicators = (defaultOpt) => {
  return new Promise((resolve, reject) => { 
    cachedFetchJson(result_framework_list_common_indicators_for_programme_path({ is_aggregate: false })).then((res) => {
      if (res){
        resolve({
          options: [defaultOpt, ...res],
          fetchingOptions: false
        })
      } else {
        reject("Invalid response")
      }
    })
  })
}

const loadIndicatorOptions = (programmeId, projectId, level, defaultOpt = {'value': 0, 'label': I18n.t('any')}) => {
  // Fund level RF indicators or project indicators
  if (level == 'fund' || projectId) {
    return getIndicators(level == 'fund' ? programmeId : projectId, defaultOpt);
  } else { // Common indicators at fund level 
    return getCommonIndicators(defaultOpt);
  }
}

export default class AdaliaMeIndicator extends GridLayout {
  static contextType = FormContext;
  
  
  static get CATEGORY() {
    return 'question';
  }
  
  static get TYPE() {
    return 'adalia_me_indicator';
  }
  
  static icon(){
    return <i className="fa fa-pencil"></i>;
  }
  
  static defaultContent(language) {
    let fieldName = 'field_' + Helpers.randomString();
    
    let valueTranslations = TextField.defaultContentTranslations(language);
    valueTranslations[language] = Object.assign(valueTranslations[language], {
      label: '',
      description: '',
      placeholder: ''
    });
    let indicatorTranslations = AdaliaMEIndicatorSelect.defaultContentTranslations(language);
    indicatorTranslations[language] = Object.assign(indicatorTranslations[language], {
      label: '',
      description: '',
      placeholder: ''
    });
    
    let dateTranslations = AdaliaMEIndicatorSelect.defaultContentTranslations(language);
    dateTranslations[language] = Object.assign(dateTranslations[language], {
      label: I18n.t('field_default_indicator_date', {scope: 'fe', locale: language}),
      description: '',
      placeholder: ''
    });
    
    let labelTranslations = Label.defaultContentTranslations(language);
    labelTranslations[language] = Object.assign(labelTranslations[language], {
      text: I18n.t('field_default_indicator_values', {scope: 'fe', locale: language}),
      description: I18n.t('field_default_indicator_values_description', {scope: 'fe', locale: language})
    });
    
    
    return Object.assign(super.defaultContent(language), {
      level: 'project',
      indicatorRows: [],
      name: fieldName,
      rows: [
        [
          {
            span: 1,
            component: Object.assign(Label.defaultContent(language), {
              name: fieldName + '_indicator_values',
              deletable: false,
              sortable: false,
              translations: labelTranslations
            })
          }
        ],
        [
          {
            span: 1,
            component: Object.assign(AdaliaMEIndicatorDate.defaultContent(language), {
              name: fieldName + '_indicator_date',
              deletable: false,
              sortable: false,
              translations: dateTranslations,
              dataType: DataType.DATE
            })
          }
        ],
        [
          {
            span: 1,
            component: Object.assign(AdaliaMEIndicatorRepeatableRegion.defaultContent(language), {
              name: fieldName + '_indicator_repetable',
              deletable: false,
              sortable: false,
              mayAdd: true,
              indicatorRows: [],
              translations: indicatorTranslations,
              component: Object.assign(AdaliaMEIndicatorSelect.defaultContent(language), {
                name: fieldName + '_indicator',
                deletable: true,
                sortable: true,
                translations: indicatorTranslations,
                component: Object.assign(AdaliaMEIndicatorValueField.defaultContent(language), {
                  name: fieldName + '_indicator_value',
                  deletable: false,
                  sortable: false,
                  reportKey: '',
                  translations: indicatorTranslations,
                })
              })
            }),                         
          }
        ]
      ]
    });
  }

  renderView() {
    return <div>
      {super.renderView()}
      {
        this.props.content.addLinkedExternalEntities && 
        <LinkedEntities 
          name={this.props.content.name} 
          disabled={this.props.mode != Mode.FILL}
        />
      }
    </div>
  }
  
  
  renderSettings() {
    return <Setting.Checkbox 
      name='addLinkedExternalEntities'
      label={I18n.t('field_add_linked_external_entities', {scope: 'fe'})} 
      value={this.props.content.addLinkedExternalEntities} 
      onChange={this.updateSettings} 
      description={I18n.t('field_add_linked_external_entities_description', {scope: 'fe'})}
    />
  }

  beforeUpdateSettings(content) {
    content.addLinkedExternalEntities = castDataType(content.addLinkedExternalEntities, DataType.BOOLEAN);
  }
}

const LinkedEntities = ({name, disabled, ...props}) => {
  const fContext = useContext(FormContext);
  const [loading, setLoading] = useState(true);
  const [externalEntityColumnDefs, setExternalEntityColumnDefs] = useState(null)
  useEffect(() => {
    if (fContext.projectId) {
      cachedFetchJson(indicator_value_get_entity_columns_for_record_path({project_id: props.project?.id || fContext.projectId})).then((res) => {
        setExternalEntityColumnDefs(res);
        setLoading(false);
      }) 
    }
  }, []);

  if (loading) return null;

  return <div className="linked-external-entity-wrapper">
    <div>
      <label>{I18n.t('common_additional_fields', {scope: 'fe'})}</label>
      <span className="description">{I18n.t('common_additional_fields_description', {scope: 'fe'})}</span>
    </div>
    <ExternalEntityInput 
      projectId={fContext.projectId}
      externalEntityColumnDefs={externalEntityColumnDefs}
      rowIdx={0}
      showFieldNames={true}
      namePrefix={`form[${name}]`}
      readOnly={disabled}
      inputSize='md'
      wrapperStyle={{display: 'inline-block', paddingRight: 16}}
    />
  </div>

}



export class AdaliaMEIndicatorGrid extends GridLayout {
  
  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      deletable: false,
      sortable: false,
      reportKey: ''
    });
  }
  
  static get TYPE() {
    return 'adalia_me_indicator_grid';
  }
  
  renderSettings() {
  }
}


export class AdaliaMEIndicatorRepeatableRegion extends RepeatableRegion {
  static contextType = FormContext;
  
  constructor(props) {
    super(props);
    let rows = [];
    
    let rowCount = props.content.indicatorRows?.length || 1
    let answer = ((this.props.answers || {})[(this.props.content || {}).name] || {});

    if (!this.inEditMode) {
        // Initial row count is the number of rows added to answers or min. required rows. Or at least 1.
        let requiredRowCount = ((this.props.content || {}).validations || {}).minRows || 0;
        let answerRowCount = (typeof answer.row_count != 'undefined') ? answer.row_count : Object.keys(answer).length;
        rowCount = Math.max(answerRowCount, requiredRowCount, 1);
    }

    if (rowCount > 1) {
      
      
      for (let i = 0; i < rowCount; i++) {
        rows.push('row-' + i);
      }
    }
    
    // Show always one row
    if (rows.length == 0) {
      rows.push('row-0');
    }
    
    this.state = {
      rows: rows,
      options: [],
      fetchingOptions: true  
    };
  }
  
  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      deletable: false,
      sortable: false,
      reportKey: ''
    });
  }
  
  static get TYPE() {
    return 'adalia_me_indicator_repetable_region';
  }
  
  /* 
   Need a custom renderView to remove the possibility to select child compononents (rows)
  */
  renderView() {
    let rows = [];
    let mayDelete = (this.state.rows.length > ((this.props.content.validations || {}).minRows || 1));
    let mayAdd = this.props.content.mayAdd;
    
    this.state.rows.forEach((rowId, i) => {
      let component = null;
      if (this.props.content.component) {
        component = Resolver.createElement(this.props.content.component.type, resolveComponentProps(this.props.content.component, this.props,  
          {
            selectCallback: (_) => { this.props.selectCallback(this.props.content) }, 
            selectedId: this.props.selectedId,
            prefillAnswers: this.props.prefillAnswers,
            repeatedFieldScope: this.props.content.name + '[row-' + i + ']',
            repeatedFieldRowIndex: i,
            indicatorConfig: (this.props.content?.indicatorRows || [])[i]
          })
          );
      } else if (this.inEditMode) {
          // component = <Helpers.DropArea componentId={this.props.content.id} />;
      }
      rows.push(
        <div key={rowId} className="component-row">
          <div className="component">
            {component}
          </div>
          <div className="delete">
            {(mayDelete && typeof this.rowIsDeletable == 'function' ? this.rowIsDeletable(i) : true) ?
            <a href="#" disabled={this.props.mode != Mode.FILL} className="btn btn-danger" onClick={this.deleteRow} data-row-id={rowId}><i className="icon-trash"></i></a>
            : ''}
            </div>
        </div>
      );
    });
      
    return (
      <div>
        <label>{this.getText('label')}</label>
        {rows}
        {mayAdd && <button type="button" className="btn btn-primary" disabled={!mayAdd || this.props.mode != Mode.FILL} onClick={mayAdd ? this.addRow : (e) => { e.preventDefault(); }}>
          <i className="icon-add"></i> {I18n.t('add_row', {scope: 'fe', locale: this.props.language})}
        </button> }
        <input type="hidden" name={this.fullFieldName() + '[row_count]'} defaultValue={rows.length} />
      </div>
      );
  }
        
  renderSettings() {
    let options = ((this.state.options).map(o => [o.value, o.label]) || []).map((o) => {
      let opt = Array.isArray(o) ? o : [o, o];
      return (<option key={opt[0]} value={opt[0]}>{opt[1]}</option>);
    });

    
    const indicatorRowElems = (this.props.content?.indicatorRows || []).map((row,idx) => {
      const disaggregations = !this.state.fetchingOptions && this.state.options.find(o => o.value == parseInt(row?.indicator || 0, 10))?.disaggregations || [];
      const disaggregation_type = !this.state.fetchingOptions && this.state.options.find(o => o.value == parseInt(row?.indicator || 0, 10))?.disaggregation_type || '';
      // const additional_fields = !this.state.fetchingOptions && this.state.options.find(o => o.value == parseInt(row?.indicator || 0, 10))?.additional_fields || [];
      return <div className="option-group" key={"indicator-row-" + idx}>
        <div className="delete" style={{float: 'right'}}>
          <a href="#" className="icon-trash" title={I18n.t('delete_option', {scope: 'fe'})} onClick={(e) => { this.deleteIndicatorRow(e, idx) } }></a>
        </div>
      <div className="group-body" style={{borderTop: 'none'}}>
        <div className="options">
          <div className='option' style={{marginBottom: 0}}>
          <div className='option-label'><b>{I18n.t('field_default_indicator', {scope: 'fe'})}</b></div>
            <div className='option-value' style={{textAlign: 'center'}}><b>{I18n.t('field_locked', {scope: 'fe'})}</b></div>
            <div className='option-value' style={{textAlign: 'center'}}><b>{I18n.t('field_required', {scope: 'fe'})}</b></div>
          </div>
          <div className='option'>
            <div className="option-label">                  
              { !this.state.fetchingOptions ? 
                <select 
                  className="form-control"
                  name={`indicatorRows|${idx}|indicator`} 
                  defaultValue={row?.indicator}
                  onChange={(e) => { this.updateIndicatorRow(idx, 'indicator', e.target.value)}} 
                  autoComplete={this.getAutoComplete()}
                >
                {options}
                </select> 
                : 
                <input type="text" disabled={true} className='form-control' value={I18n.t('loading')}/>
              }
            </div>
            <div className="option-value">
              <input 
                type="checkbox" 
                className="for2m-control"
                defaultChecked={row?.locked}
                onChange={(e) => { this.updateIndicatorRow(idx, 'locked', e.target.checked)}} 
              />
            </div>
            <div className="option-value">
              <input 
                type="checkbox" 
                className="for2m-control"
                defaultChecked={row?.required}
                onChange={(e) => { this.updateIndicatorRow(idx, 'required', e.target.checked)}} 
              />
            </div>
          </div>
          <div className='option' style={{marginBottom: 0}}>
            <div className='option-label'><b>{I18n.t('field_description_key', {scope: 'fe'})}</b></div>
            <div className='option-label'><b>{I18n.t('field_value_key', {scope: 'fe'})}</b></div>
          </div>
          <div className='option'>
            <div className="option-label">
              <input 
                type="text" 
                name={`indicatorRows|${idx}|descriptionReportKey`} 
                defaultValue={row?.descriptionReportKey}
                onChange={(e) => { this.updateIndicatorRow(idx, 'descriptionReportKey', e.target.value)}}
                disabled={!this.allowModifyInSafeMode}
                className="form-control"
                autoComplete={this.getAutoComplete()}
              />
            </div>
            <div className="option-label">
              <input 
                type="text" 
                name={`indicatorRows|${idx}|valueReportKey`} 
                defaultValue={row?.valueReportKey}
                onChange={(e) => { this.updateIndicatorRow(idx, 'valueReportKey', e.target.value)}}
                disabled={!this.allowModifyInSafeMode}
                className="form-control"
                autoComplete={this.getAutoComplete()}
              />
            </div>
          </div>

          { // Render disaggregations for selectin default values
            disaggregations.length > 0 && disaggregation_type != 'lax' && <>
              <div className='option'>
                <div className='option-label'><b>{I18n.t('field_disaggregations', {scope: 'fe'})}</b></div>
              </div>
              <div className='option'>
                <div className="option-label">
                  {disaggregations.map(d => 
                    <select
                        key={'da-' + d.dimension_id} 
                        className="form-control"
                        name={`indicatorRows|${idx}|disaggregations|${d.dimension_id}|disaggregation`}
                        defaultValue={(row.disaggregations || {})[d.dimension_id]}
                        onChange={(e) => { this.updateIndicatorRowDisaggregation(idx, 'disaggregations', d.dimension_id, e.target.value)}} 
                        autoComplete={this.getAutoComplete()}
                      >
                        <option value={0}>{I18n.t('select_disaggregation', {dimension: d.dimension})}</option>
                      {d.categories.map(c => <option key={'dc-' + c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  )} 
                </div>
              </div>
            </>
          }
        </div>
      </div>
    </div>
    }
    );
    
    return (
      <div>
        <Setting.Dropdown
          name="indicatorLevel"
          label={I18n.t('indicator_level_label', {scope: 'fe'})}
          value={this.props.content.level || 'project'}
          onChange={(e) => this.updateLevel(e.target.value)}
          options={INDICATOR_LEVEL_OPTIONS.map(o => [o, I18n.t(o)])} 
        />
        
        <Setting.Checkbox name="mayAdd" label={I18n.t('field_may_add_new', {scope: 'fe'})} value={this.props.content.mayAdd} onChange={this.updateSettings} />

        <div className='setting'>
          <div className="option-groups">
            {indicatorRowElems}
          </div>
          <div className="add-buttons">
            <a href="#" className="btn btn-primary" onClick={this.addIndicatorRow}>
              <i className="icon-add"></i> {I18n.t('add_indicator_row', {scope: 'fe'})}
            </a>
          </div>
        </div>


      </div>
    )
  }
          
          
  rowIsDeletable = (idx) => {
    return !(this.props.content.indicatorRows || [])[idx]?.required
  }
  
  componentDidMount() {
    if ([Mode.SETTINGS].includes(this.props.mode)) { 
      if (this.state.options.length == 0) {
        this.handleIndicatorOptionLoad();
      }
    }
  }
  
  componentDidUpdate(prevProps, _) {
    if (this.props.content.indicatorRows && (
        ((this.props.content.indicatorRows || []).length > this.state.rows.length) || 
        (this.props.content.indicatorRows.length != 0 && this.props.content.indicatorRows.length != this.state.rows.length && this.props.mode == Mode.EDIT)
      )
    ) {
      this.initRows();
    }
    
    if ([Mode.SETTINGS].includes(this.props.mode) && prevProps.content.level != this.props.content.level) {
      this.handleIndicatorOptionLoad();
    }
  }

  initRows = () => {
    let rows = [];
    let rowCount = this.props.content.indicatorRows.length || 1
    for (let i = 0; i < rowCount; i++) {
      rows.push('row-' + i);
    }
    
    this.setState({
      rows: rows
    });
  }
  
  
  addIndicatorRow = (event) => {
    let content = JSON.parse(JSON.stringify(this.props.content));
    content.indicatorRows = content.indicatorRows || []
    content.indicatorRows.push({});
    this.props.updateSettingsCallback(this.props.content.id, content, true)
    event.preventDefault();
  }
  
  deleteIndicatorRow = (event, idx) => {
    let content = JSON.parse(JSON.stringify(this.props.content));
    content.indicatorRows.splice(idx, 1);
    this.props.updateSettingsCallback(this.props.content.id, content, true)
    event.preventDefault();
  }

  updateIndicatorRow = (rowIdx, field, value) => {
    let content = JSON.parse(JSON.stringify(this.props.content));
    content.indicatorRows[rowIdx][field] = value;
    this.props.updateSettingsCallback(this.props.content.id, content, true)
  }

  updateIndicatorRowDisaggregation = (rowIdx, field, dimensionId, value) => {
    let content = JSON.parse(JSON.stringify(this.props.content));
    content.indicatorRows[rowIdx][field] = content.indicatorRows[rowIdx][field] || {}
    content.indicatorRows[rowIdx][field][dimensionId] = value;
    this.props.updateSettingsCallback(this.props.content.id, content, true)
  }
  
  updateLevel(level) {
    let content = JSON.parse(JSON.stringify(this.props.content));
    content.level = level;
    // Pass also the underlying indicator select
    content.component.level = level;
    this.props.updateSettingsCallback(this.props.content.id, content, true)
    this.setState({fetchingOptions: false, options: []})
  }
  
  handleIndicatorOptionLoad = () => {
    const programmeId = this.context.programmeId
    const projectId = this.context.projectId
    loadIndicatorOptions(programmeId, projectId, this.props.content.level).then(res => {
      this.setState(res)
    }).catch(e => {
      alert(e);
    })
  }
  beforeUpdateSettings(content) {
    super.beforeUpdateSettings(content);
    content.mayAdd = castDataType(content.mayAdd, DataType.BOOLEAN);
  }


}
        
export class AdaliaMEIndicatorValueField extends TextField {
  static defaultContent(language) {
    let valueLabelTranslations = Label.defaultContentTranslations(language);
    valueLabelTranslations[language] = Object.assign(valueLabelTranslations[language], {
      text: I18n.t('field_default_indicator_value', {scope: 'fe', locale: language}),
      description: ''
    });
    
    return Object.assign(super.defaultContent(language), {
      deletable: false,
      sortable: false,
      reportKey: '',
      translations: valueLabelTranslations,
      dataType: DataType.DECIMAL,
      validations: { dataType: "decimal" }
    });
  }
  
  static get TYPE() {
    return 'adalia_me_indicator_value';
  }
  
  renderSettings() {
  }

  getReportKey() {
    return this.props.indicatorConfig?.valueReportKey;
  }

}
export class AdaliaMEIndicatorDescriptionField extends TextField {
  static defaultContent(language) {
    
    
    let descriptionLabelTranslations = Label.defaultContentTranslations(language);
    descriptionLabelTranslations[language] = Object.assign(descriptionLabelTranslations[language], {
      text: I18n.t('field_default_indicator_description', {scope: 'fe', locale: language}),
      description: ''
    });
    
    return Object.assign(super.defaultContent(language), {
      deletable: false,
      sortable: false,
      reportKey: '',
      charLimit: 255,
      translations: descriptionLabelTranslations
    });
  }            
  static get TYPE() {
    return 'adalia_me_indicator_value_description';
  }
  
  renderSettings() {
  }

  getReportKey() {
    return this.props.indicatorConfig?.descriptionReportKey;
  }

}
export class AdaliaMEIndicatorDate extends TextField {
  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      deletable: false,
      sortable: false,
      reportKey: '',
      dataType: DataType.DATE
    });
  }
  
  static get TYPE() {
    return 'adalia_me_indicator_value_date';
  }
  
  renderSettings() {
    return <div>
      <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
      <Setting.TextField name="placeholder" label={I18n.t('field_placeholder', {scope: 'fe'})} value={this.getText('placeholder')} onChange={this.updateSettings} />
      <Setting.LongTextField name="description" label={I18n.t('field_description', {scope: 'fe'})} value={this.getText('description')} onChange={this.updateSettings} />
      <Setting.LongTextField name="help" label={I18n.t('field_help', {scope: 'fe'})} value={this.getText('help')} onChange={this.updateSettings} />
      <Setting.TextField name="reportKey" label={I18n.t('field_report_key', {scope: 'fe'})} value={this.props.content.reportKey} onChange={this.updateSettings} description={I18n.t('field_report_key_description', {scope: 'fe'})} />
    </div>
  }
}


export class AdaliaMEIndicatorSelect extends DropdownSelectionWithAsyncOptions {
  
  static contextType = FormContext;
  
  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      deletable: false,
      sortable: false,
      reportKey: ''
    });
  }
  
  static get TYPE() {
    return 'adalia_me_indicator_select';
  }

  static nestedComponents(parentComponentContent){
    let c = (parentComponentContent || {}).component;
    
    return c ? [c] : [Object.assign(AdaliaMEIndicatorValueField.defaultContent('en'), {
      deletable: false,
      sortable: false,
      reportKey: '',
    })] ;
  }
  
  
  // Take the whole row as the fields are dynamic based on the selection.
  static resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope = '') {
    if (!content) { return [] }
    let names = [];
    names.push(repeatedFieldScope); 
    return names;
  }
  
  
  renderSettings() {
  }

  renderDisaggregations = () => {
    const selectedIndicator = this.getSelectedOptions();    
    
    if (!selectedIndicator || !selectedIndicator.disaggregations || selectedIndicator.disaggregations.length == 0){
      return null;
    }
    
    let disaggregationTranslations = DropdownSelection.defaultContentTranslations(this.props.language);
    disaggregationTranslations[this.props.language] = Object.assign(disaggregationTranslations[this.props.language], {
      label: '',
      description: '',
      placeholder: ''
    });
    selectedIndicator.disaggregations.forEach(idd => {
      if (idd.categories){
        idd.categories.forEach(idc => disaggregationTranslations[this.props.language][idc.label] = idc.label)
      }
    })

    
    const colNum = selectedIndicator.disaggregation_type == 'lax' ? 
    Math.floor(12 / selectedIndicator.disaggregations.map(idd => idd.categories.length).reduce((s, n) => s + n, 0))
      : 
      Math.floor(12 / selectedIndicator.disaggregations.length);

    return <>
      <div className="col-xs-12 mt-1">
        <label>{I18n.t('disaggregations', {locale: this.props.language})}</label>
      </div>
      {selectedIndicator.disaggregations.map(idd => {
        if (selectedIndicator.disaggregation_type == 'lax') {
          return <div key={`disaggregation-dimension-${idd.dimension_id}`} >
            {idd.categories.map(c => <div key={`disagg-category-${c.value}`} className={`col-xs-${colNum}`}>
              <TextField
                {...this.props}
                content={Object.assign(TextField.defaultContent(this.props.language), {
                  name: `${this.props.content.name}_additional_information[lax_disagg:${c.value}]`,
                  dataType: DataType.INTEGER,
                  translations: {[this.props.language]: {...Object.assign(disaggregationTranslations[this.props.language], {})}}
                })}
              />
              <p className='bold-description'>{`${idd.dimension} - ${c.label}`}</p>
            </div>
            )}
          </div>
        } else  {
          const filteredOptions = this.filterDisaggregationCategories(idd.categories, idd.dimension_id);
          return <div key={`disaggregation-dimension-${idd.dimension_id}`} className={`col-xs-${colNum}`}>
            <DropdownSelection
              {...this.props}
              content={Object.assign(DropdownSelection.defaultContent(this.props.language), {
                name: this.props.content.name + '_disaggregation_' + idd.dimension_id,
                deletable: false,
                sortable: false,
                options: [ { dependency: '_default', options: filteredOptions  } ],
                includeBlank: filteredOptions.length > 1,
                translations: {[this.props.language]: {...disaggregationTranslations[this.props.language], blankOptionLabel: I18n.t('select_disaggregation', {dimension: idd.dimension})}},
                blankOptionLabel: idd.dimension
              })}
            />
          </div>
        }
      })}
    </>
    
  }

  renderAdditionalFields = () => {
    const selectedIndicator = this.getSelectedOptions();
    if (!selectedIndicator || !selectedIndicator.additional_fields || selectedIndicator.additional_fields.length == 0){
      return null;
    }
    // selectedIndicator.additional_fields.map((af)=>{
    //   Object.assign(af, {value: af.id });
    //   Object.assign(af, {label: af.name });
    // })

    let afTranslations = DropdownSelection.defaultContentTranslations(this.props.language);
    afTranslations[this.props.language] = Object.assign(afTranslations[this.props.language], {
      label: '',
      description: '',
      placeholder: ''
    });
    selectedIndicator.additional_fields.forEach(af => {
        afTranslations[this.props.language][af.name] = af.name
    })

    return <>
      <div className="col-xs-12 mt-1">
        <label>{I18n.t('additional_fields', {locale: this.props.language})}</label>
      </div>
        {selectedIndicator.additional_fields.map(af => 
          <div key={`additional-fields-${af.name}`} className='col-xs-6' >
            <TextField
              {...this.props}
              content={Object.assign(TextField.defaultContent(this.props.language), {
                name: `${this.props.content.name}_additional_information[${af.id}]`,
                dataType: DataType.INTEGER,
                translations: {[this.props.language]: {...Object.assign(afTranslations[this.props.language], {})}}
              })}
            />
            <p className='bold-description'>{af.name}</p>
          </div>
        )}
    </>
  }
  
  renderView() {
    const selectedIndicator = this.getSelectedOptions();
    const project_id = this.context.projectId;
    const answer = this.getAnswer();
    return <div className="indicator-select-wrapper">
      <div className="row">
        <div className={`col-xs-5 indicator-select`}>
          <label>{I18n.t('field_default_indicator', {scope: 'fe', locale: this.props.language})}</label>
        
          {super.renderView()}
          {/* check if is loaded from app or system by check project_id. TODO: add public route loading indicator details */}
          {
            project_id && answer && (!Array.isArray(answer) || answer.length > 0) && <a 
            title={I18n.t('indicator_details')}
            data-modal-class="modal-lg" 
            data-title={I18n.t('indicator_details')}
            data-hide-buttons="true" 
            data-content="" 
            className="modal-dialog-link btn btn-default info-button" 
            href={indicator_value_show_path({indicator_id: answer})}
            ><FaIcon type='info-circle'/></a>     
          }
        </div>
        <div className={`col-xs-4`}>
          <label>{I18n.t('field_default_indicator_description', {scope: 'fe', locale: this.props.language})}</label>
          
          { selectedIndicator && <AdaliaMEIndicatorDescriptionField 
          {...this.props}
          selectCallback={this.props.selectCallback}
          content={Object.assign(AdaliaMEIndicatorDescriptionField.defaultContent(this.props.language), {
            name: this.props.content.name + '_description',
            deletable: false,
            sortable: false,
          })}
          /> }
        </div>
        <div className={`col-xs-3`}>
          <label>{I18n.t('field_default_indicator_value', {scope: 'fe', locale: this.props.language})}</label>
          
          { selectedIndicator && <AdaliaMEIndicatorValueField 
            {...this.props}
            selectCallback={this.props.selectCallback}
            content={Object.assign(AdaliaMEIndicatorValueField.defaultContent(this.props.language), {
              name: this.props.content.name + '_value',
              deletable: false,
              sortable: false,
              placeholder: selectedIndicator ? selectedIndicator.unit_name : ''
            })}
            /> }
        </div>
        {this.renderDisaggregations()}
        {this.renderAdditionalFields()}
      </div>
    </div>
  }

  // Filter indicator options based on configurations per row.
  filterIndicatorOptions = (arr) => {
    if (this.props.indicatorConfig?.locked && (this.props.indicatorConfig || {}).indicator != 0){
      // Filter indicator options. When loaded in project context, check based on common_indicator_id 
      arr = arr.filter(i => parseInt(i.common_indicator_id || i.value, 10) == parseInt((this.props.indicatorConfig || {}).indicator, 10) )
    }
    let answer = super.getAnswer();

    // If no user selected answer & indicator configured & in fill mode(answerCallBack exists )
    if (!answer && this.props.indicatorConfig?.indicator && this.props.updateAnswerCallback) {
      var defaultValue = arr.find(a => (a.common_indicator_id || a.value) == this.props.indicatorConfig.indicator);
      this.props.updateAnswerCallback(this.fullFieldName(), defaultValue?.value);
      // Also set the default values for disaggregations
      Object.keys(this.props.indicatorConfig?.disaggregations || {} ).forEach(dimensionId => {
        this.props.updateAnswerCallback(
          // Use replace to figure out the correct full name for each dimension. +']]' to only match last field name.
          this.fullFieldName().replace(this.props.content.name + ']]', this.props.content.name + '_disaggregation_' + dimensionId + ']]'), 
          this.props.indicatorConfig?.disaggregations[dimensionId]
        );        
      })
    }
    
    return arr
  }

  filterDisaggregationCategories = (categories, dimensionId) => {
    const lockedCategory = this.props.indicatorConfig?.locked && (this.props.indicatorConfig?.disaggregations || {})[dimensionId]
    if (lockedCategory && lockedCategory != 0){ // 0 is the "Please select option."
      categories = categories.filter(c => c.value == parseInt(lockedCategory,10));
    }
    return categories;
  }

  componentDidMount() {
    super.componentDidMount();
    
    const isFundIndicator = this.props.content.level == 'fund'
    // Load based on level, default to fund for "project" level to display common indicators in Preview mode
    let targetId = isFundIndicator ? this.context.programmeId : (this.context.projectId || this.context.programmeId);
    
    if ([Mode.EDIT].includes(this.props.mode) && !isFundIndicator){
      // Load dummy data if editing form for project variables
      this.setState({options: SETTINGS_DUMMY_DATA, loading: false})
    } else {
       /* 
        * The async fetch doesn't work for mobile app version of the form
        * Expected to be found in DOM
        */
      let results = isFundIndicator ? jQuery('#public-form').data('fund-results') : jQuery('#public-form').data('results')
      if(results){
        var arr = this.filterIndicatorOptions(results, arr, 0);
        this.setState({
          options: groupBy(arr, 'group_by'),
          loading: false,
        }, this.initSelect2)
      }
      else{
        if(targetId){
          const programmeId = this.context.programmeId
          const projectId = this.context.projectId
      
          loadIndicatorOptions(programmeId, projectId, this.props.content.level, {value: 0, label: I18n.t('select_blank_option_default', {scope: 'fe'})}).then((res) => {
            
              this.setState({
                options: this.filterIndicatorOptions(res.options),
                loading: false,
              }, this.initSelect2)
          })
        }
        else{
          this.setState({options: SETTINGS_DUMMY_DATA, loading: false})
        }
      }
    }
  }
}